import React, { useState, useRef, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { useUpdateProfile } from "./hooks";

interface FieldBadgeProps {
  profileId: number;
  profileType: string;
  fieldName: string;
  label: string;
  value: any;
  children: React.ReactNode; // Form fields
}

export function FieldBadge({
  profileId,
  profileType,
  fieldName,
  label,
  value,
  children,
}: FieldBadgeProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const updateProfile = useUpdateProfile();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const hasValue = () => {
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === "string") return value.length > 0;
    if (typeof value === "number") return value > 0;
    if (typeof value === "boolean") return true;
    return !!value;
  };

  const badgeClass = hasValue()
    ? "inline-flex text-sm font-medium items-center gap-1 px-3 py-1 rounded-full cursor-pointer transition-colors bg-default-50 text-default-600 hover:bg-default-200"
    : "inline-flex text-sm font-medium items-center gap-1 px-3 py-1 rounded-full cursor-pointer transition-colors bg-slate-50 text-slate-500 hover:bg-slate-200";

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const formData = new FormData(e.currentTarget);
    const data: any = {};

    // Properly handle array fields
    formData.forEach((value, key) => {
      // Remove [] suffix if present
      const cleanKey = key.replace(/\[\]$/, "");

      // If key already exists, convert to array or append
      if (data[cleanKey] !== undefined) {
        if (Array.isArray(data[cleanKey])) {
          data[cleanKey].push(value);
        } else {
          data[cleanKey] = [data[cleanKey], value];
        }
      } else {
        // Check if this is an array field (has [] suffix or multiple values coming)
        if (key.endsWith("[]")) {
          data[cleanKey] = [value];
        } else {
          data[cleanKey] = value;
        }
      }
    });

    updateProfile.mutate(
      { profileId, profileType, data },
      {
        onSuccess: () => {
          setIsOpen(false);
        },
      },
    );
  };

  return (
    <div className="relative inline-block" ref={dropdownRef}>
      <span className={badgeClass} onClick={() => setIsOpen(!isOpen)}>
        {label}
        <ChevronDown className="w-3.5 h-3.5" />
      </span>

      {isOpen && (
        <div className="absolute left-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 z-50">
          <div className="p-4 pt-0 w-[400px]">
            <form onSubmit={handleSubmit} className="space-y-4">
              {children}
              <div className="flex items-center justify-end">
                <button
                  type="submit"
                  disabled={updateProfile.isPending}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-default-600 rounded hover:bg-default-700 disabled:opacity-50"
                >
                  {updateProfile.isPending ? "Mise à jour..." : "Mettre à jour"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
